import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Calculator, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "../context/SiteContext";
import { api } from "../lib/site";
import { Reveal, ChapterTag, SectionTitle, EASE } from "./Reveal";

const FACTORS = ["Panjang", "Lebar", "Tinggi", "Temperatur", "Jenis produk", "Kapasitas", "Jenis panel", "Jenis refrigeration system", "Lokasi pemasangan"];
const STORAGE_TYPES = ["Chiller Room (+2°C s/d +8°C)", "Freezer Room (-18°C s/d -25°C)", "Blast Freezer (-35°C s/d -40°C)", "Ice Storage", "Custom Cold Room"];
const BUSINESS_TYPES = ["Restoran", "Hotel", "Supermarket / Minimarket", "Distributor Makanan", "Industri Makanan & Minuman", "Supplier Seafood", "Supplier Daging", "Peternakan", "Perikanan", "Frozen Food", "UMKM", "Pabrik / Industri", "Lainnya"];

const initialForm = { name: "", wa: "", company: "", business: "", storage_type: STORAGE_TYPES[0], length: "", width: "", height: "", temperature: "", location: "", message: "" };

export function QuoteSection() {
  const { wa } = useSite();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const estimate = useMemo(() => {
    const v = (parseFloat(form.length) || 0) * (parseFloat(form.width) || 0) * (parseFloat(form.height) || 0);
    if (v <= 0) return null;
    const t = form.storage_type;
    const factor = t.includes("Blast") ? 0.14 : t.includes("Freezer") ? 0.09 : 0.05;
    return Math.max(1, Math.round(v * factor * 10) / 10);
  }, [form.length, form.width, form.height, form.storage_type]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", { ...form, type: "quotation" });
      const lines = [
        "Halo, saya ingin meminta penawaran Cold Storage.",
        "",
        `Nama: ${form.name}`,
        `No. WhatsApp: ${form.wa}`,
        form.company && `Perusahaan: ${form.company}`,
        form.business && `Jenis Usaha: ${form.business}`,
        `Jenis Cold Storage: ${form.storage_type}`,
        (form.length || form.width || form.height) && `Dimensi (PxLxT): ${form.length || "-"} x ${form.width || "-"} x ${form.height || "-"} m`,
        form.temperature && `Target Temperatur: ${form.temperature}`,
        form.location && `Lokasi: ${form.location}`,
        estimate && `Estimasi kebutuhan (kalkulator web): ±${estimate} HP`,
        form.message && `Keterangan: ${form.message}`,
      ].filter(Boolean);
      window.open(wa(lines.join("\n")), "_blank");
      toast.success("Permintaan penawaran terkirim. Anda diarahkan ke WhatsApp.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Gagal mengirim. Silakan coba lagi atau hubungi WhatsApp kami langsung.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-navy-900 border border-glacier/20 rounded-sm px-4 py-3 text-sm text-frost placeholder:text-steel/50 focus:outline-none focus:border-ice transition-colors";

  return (
    <section id="penawaran" data-testid="calculator-section" className="relative py-24 sm:py-32 bg-navy-900 noise-overlay">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <ChapterTag number="08" label="Estimasi & Penawaran" />
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div>
            <SectionTitle>Berapa Biaya Membuat <span className="text-ice">Cold Storage?</span></SectionTitle>
            <Reveal delay={0.15}>
              <p className="mt-6 text-steel text-base sm:text-lg leading-relaxed">
                Karena setiap kebutuhan berbeda, harga tidak bisa disamaratakan. Biaya pembuatan cold storage dipengaruhi oleh:
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-7 flex flex-wrap gap-2.5" data-testid="quote-factors">
                {FACTORS.map((f) => (
                  <span key={f} className="font-mono text-xs tracking-wide text-glacier border border-glacier/25 rounded-sm px-3.5 py-2">
                    {f}
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <button
                data-testid="quote-open-form-btn"
                onClick={() => setOpen(true)}
                className="mt-9 inline-flex items-center gap-2.5 bg-ice text-navy-950 font-semibold px-7 py-4 rounded-sm hover:bg-glacier transition-colors glow-ice"
              >
                <Calculator className="w-5 h-5" />
                Minta Estimasi Harga Gratis
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
              </button>
            </Reveal>
          </div>

          <AnimatePresence>
            {open && (
              <motion.form
                data-testid="quote-form"
                onSubmit={submit}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.45, ease: EASE }}
                className="overflow-hidden lg:col-span-1"
              >
                <div className="bg-navy-950 border border-glacier/25 rounded-sm p-6 sm:p-8 space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input data-testid="quote-input-nama" required value={form.name} onChange={set("name")} placeholder="Nama *" className={inputCls} />
                    <input data-testid="quote-input-wa" required value={form.wa} onChange={set("wa")} placeholder="Nomor WhatsApp *" className={inputCls} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input data-testid="quote-input-perusahaan" value={form.company} onChange={set("company")} placeholder="Nama Perusahaan" className={inputCls} />
                    <select data-testid="quote-input-usaha" value={form.business} onChange={set("business")} className={inputCls}>
                      <option value="">Jenis Usaha</option>
                      {BUSINESS_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <select data-testid="quote-input-jenis" value={form.storage_type} onChange={set("storage_type")} className={inputCls}>
                    {STORAGE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <div className="grid grid-cols-3 gap-4">
                    <input data-testid="quote-input-panjang" type="number" min="0" step="0.1" value={form.length} onChange={set("length")} placeholder="Panjang (m)" className={inputCls} />
                    <input data-testid="quote-input-lebar" type="number" min="0" step="0.1" value={form.width} onChange={set("width")} placeholder="Lebar (m)" className={inputCls} />
                    <input data-testid="quote-input-tinggi" type="number" min="0" step="0.1" value={form.height} onChange={set("height")} placeholder="Tinggi (m)" className={inputCls} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input data-testid="quote-input-temperatur" value={form.temperature} onChange={set("temperature")} placeholder="Target Temperatur (mis. -20°C)" className={inputCls} />
                    <input data-testid="quote-input-lokasi" value={form.location} onChange={set("location")} placeholder="Lokasi Pemasangan" className={inputCls} />
                  </div>
                  <textarea data-testid="quote-input-keterangan" value={form.message} onChange={set("message")} placeholder="Keterangan tambahan (jenis produk, kapasitas, dll)" rows={3} className={inputCls} />

                  {estimate && (
                    <p className="font-mono text-xs text-ice border border-ice/30 bg-ice/5 rounded-sm px-4 py-3" data-testid="quote-estimate">
                      Estimasi kasar kebutuhan mesin: ±{estimate} HP — angka indikatif, kebutuhan aktual dihitung tim kami.
                    </p>
                  )}

                  <button
                    data-testid="quote-submit-btn"
                    type="submit"
                    disabled={sending}
                    className="w-full flex items-center justify-center gap-2.5 bg-ice text-navy-950 font-semibold px-6 py-4 rounded-sm hover:bg-glacier transition-colors disabled:opacity-60"
                  >
                    <Send className="w-4 h-4" />
                    {sending ? "Mengirim..." : "Kirim Permintaan Penawaran"}
                  </button>
                  <p className="text-[11px] text-steel/70 text-center">Data Anda tersimpan aman & langsung diteruskan ke WhatsApp kami.</p>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
